import { useEffect } from "react";
import { Link } from "react-router-dom";
import { ChevronRight } from "lucide-react";
import Footer from "@/components/Footer";
import SeoHead from "@/components/SeoHead";

const AccessibilityStatementPage = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="min-h-screen bg-gradient-to-b from-sky-50 to-amber-50" data-testid="accessibility-statement-page">
      <SeoHead
        title="Accessibility Statement"
        description="PublicBeachAccess.com is committed to WCAG 2.1 Level AA accessibility. Our site includes adjustable text size, high contrast modes, link highlighting, and keyboard navigation. Learn about our accessibility tools and how to report issues."
        path="/accessibility-statement"
      />

      <div className="bg-white border-b border-gray-200">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <nav className="flex items-center gap-2 text-sm text-gray-500 mb-4">
            <Link to="/" className="hover:text-[#0EA5E9]">Home</Link>
            <ChevronRight className="w-4 h-4" />
            <span className="text-gray-800 font-medium">Accessibility Statement</span>
          </nav>
          <h1 className="text-3xl sm:text-4xl font-extrabold text-gray-800 font-['Outfit']">
            Accessibility Statement
          </h1>
          <p className="text-gray-500 mt-2">Last Updated: February 27, 2026</p>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="bg-white rounded-2xl shadow-lg p-6 sm:p-8 space-y-8 text-gray-700">

          <section>
            <h2 className="text-xl font-bold text-gray-800 font-['Outfit'] mb-3">Our Commitment to Accessibility</h2>
            <p className="leading-relaxed">
              Digital Properties, LLC is committed to ensuring digital accessibility for people with disabilities. We are continually improving the user experience for everyone and applying relevant accessibility standards to enhance usability and inclusivity. Our goal is to provide a website that is accessible to the widest possible audience, regardless of technology or ability.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-bold text-gray-800 font-['Outfit'] mb-3">Accessibility Standards</h2>
            <p className="leading-relaxed">
              We strive to conform to the Web Content Accessibility Guidelines (WCAG) 2.1 Level AA, which are widely recognized as the industry standard for website accessibility. Accessibility is an ongoing process, and we are actively working toward improving the accessibility and usability of our website.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-bold text-gray-800 font-['Outfit'] mb-3">Accessibility Technology & Tools</h2>
            <p className="leading-relaxed mb-3">
              To support our accessibility efforts, we have deployed industry-standard accessibility technology designed to enhance usability and assist visitors with disabilities. Our built-in accessibility widget provides:
            </p>
            <ul className="list-disc list-inside space-y-2 ml-2">
              <li><strong>Text Size Controls</strong> — Increase or decrease text size from 70% to 150%</li>
              <li><strong>Contrast Modes</strong> — Switch between Normal, High Contrast, Dark, and Inverted color schemes</li>
              <li><strong>Link Highlighting</strong> — Toggle visible outlines on all clickable links for improved navigation</li>
              <li><strong>Big Cursor</strong> — Enable an enlarged cursor for easier pointer visibility</li>
              <li><strong>Keyboard Navigation</strong> — Full support for tab-based navigation and focus indicators</li>
            </ul>
            <p className="leading-relaxed mt-3">
              Access these tools anytime by clicking the accessibility icon in the bottom-left corner of every page.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-bold text-gray-800 font-['Outfit'] mb-3">Ongoing Improvement</h2>
            <p className="leading-relaxed mb-3">
              Accessibility is an ongoing initiative. We are:
            </p>
            <ul className="list-disc list-inside space-y-2 ml-2">
              <li>Reviewing site content and functionality regularly</li>
              <li>Working with developers and accessibility tools</li>
              <li>Identifying and remediating barriers</li>
              <li>Incorporating accessibility into future design and development</li>
            </ul>
            <p className="leading-relaxed mt-3">
              We understand accessibility is a continuous journey and are committed to making improvements over time.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-bold text-gray-800 font-['Outfit'] mb-3">Third-Party Content</h2>
            <p className="leading-relaxed">
              Some areas of the website may include third-party content, plugins, or integrations that are not fully controlled by us. While we encourage vendors to provide accessible content, we cannot guarantee accessibility of third-party features. However, we welcome feedback regarding any issues encountered.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-bold text-gray-800 font-['Outfit'] mb-3">Feedback & Assistance</h2>
            <p className="leading-relaxed mb-3">
              If you experience difficulty accessing any part of this website, need assistance, or would like to request content in an alternative format, please contact us at{" "}
              <a href="mailto:hello.digitalproperties@gmail.com" className="text-[#0EA5E9] hover:underline">hello.digitalproperties@gmail.com</a>{" "}
              or through our <Link to="/contact" className="text-[#0EA5E9] hover:underline">Contact Page</Link>. Please include:
            </p>
            <ul className="list-disc list-inside space-y-2 ml-2">
              <li>The page URL where you encountered the issue</li>
              <li>A description of the accessibility barrier</li>
              <li>Your preferred contact method</li>
            </ul>
            <p className="leading-relaxed mt-3">
              We aim to respond within a reasonable timeframe and will make reasonable efforts to provide accessible alternatives.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-bold text-gray-800 font-['Outfit'] mb-3">Good Faith Efforts</h2>
            <p className="leading-relaxed">
              We are making good-faith efforts to comply with accessibility standards and improve usability for individuals with disabilities. Despite our efforts, some content may not yet fully meet the strictest accessibility standards due to the evolving nature of technology and ongoing updates. We appreciate your understanding as we continue improving accessibility.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-bold text-gray-800 font-['Outfit'] mb-3">Terms & Conditions — Accessibility Clause</h2>
            <p className="leading-relaxed">
              Digital Properties, LLC is committed to providing an accessible website experience for all users and strives to follow recognized accessibility standards, including WCAG guidelines. We have implemented industry-standard accessibility tools and continue to review, update, and improve our website to enhance accessibility. Because accessibility is an ongoing process, some areas of the website may not yet fully conform to all accessibility standards. If you encounter accessibility barriers, please contact us so we can provide assistance, offer alternative access, and address the issue where feasible. Nothing in these Terms is intended to limit any rights available under applicable accessibility laws.
            </p>
          </section>

        </div>
      </div>

      <Footer />
    </div>
  );
};

export default AccessibilityStatementPage;
